import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewEncapsulation, signal } from "@angular/core";
import { Router } from "@angular/router";

import { AddEvent,  GridDataResult, GridModule } from "@progress/kendo-angular-grid";
import { State } from "@progress/kendo-data-query";
import { forkJoin, Observable, Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";

import { AuthorizationService } from "@myapp/modules/fwcore/services";

import { AsyncPipe, NgIf } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "@progress/kendo-angular-buttons";
import { ComboBoxModule } from "@progress/kendo-angular-dropdowns";
import { LabelModule } from "@progress/kendo-angular-label";
import {
    AuthPermIdentifier, DatePreferencePipe, EmptyNumberFormatPipe,
    NotificationDialogService,
    NullableBooleanClassDirective, RequestUserPermission
} from "../../../../modules/fwcore";

import { ControlResizerOptions, ControlResizerService } from "../../../../../services/framework";
import { TriStateCheckBoxComponent, ClickFilterComponent } from "../../../widgets";
import { LicenseGridEditorComponent } from "../../components/licenses/license-grid-editor/license-grid-editor.component";
import { Company, ICompany, License, RequestLicense } from "../../models";
import { AdminService, LicenseServiceGrid } from "../../services";
import { IsgSizerDirective } from "../../../prob/directives/isg-sizer.directive";
import { IsgLoaderComponent } from "../../../../modules/fwcore/components/isg-loader/isg-loader.component";
import { HttpResponse } from "@angular/common/http";


@Component({
  selector: 'app-page-license',
  standalone: true,
  imports: [GridModule, NgIf, LabelModule, ComboBoxModule, FormsModule, IsgSizerDirective, TriStateCheckBoxComponent, ClickFilterComponent
    , ButtonModule, NullableBooleanClassDirective,  LicenseGridEditorComponent
    , AsyncPipe, DatePreferencePipe, EmptyNumberFormatPipe, IsgLoaderComponent],
  templateUrl: './page-license.component.html',
  styleUrls: ['./page-license.component.scss']
})

export class PageLicenseComponent implements OnInit, OnDestroy {

  /*-------------------------- Input / Outputs --------------------------------*/



  /*-------------------------- public properties / methods --------------------*/

  /*-------------------------- Internal properties  ---------------------------*/

  protected editDataItem?: License;

  protected destroy$ = new Subject();

  protected licActive?: boolean | null = true;

  protected view: Observable<GridDataResult>;

  protected isNew?: boolean;

  protected selectedCompanyItem?: Company;

  protected companies: ICompany[] | null = null;

  protected req: RequestLicense = {
    skip: 0
    , take: 15
  };

  protected reqPerm: RequestUserPermission = {

  }

  protected perms?: AuthPermIdentifier[] = [];

  /*-------------------------- State & permissions  --------------------------*/

  protected state: State = {
    skip: 0
    , take: 15
    , sort: []
  }

  protected showAdd: boolean = false;
  protected showEditButton: boolean = false;
  protected showReadOnlyButton: boolean = false;
  protected showOneSysSettings: boolean = false;

  /** Controls visibility of the full-page loading overlay. */
  protected loadingPanelVisible = signal<boolean>(false);

  /** Title used for the user download dialogs. */
  protected readonly ctDownloadDialogTitle = 'Download Users';

  /** File extension for the user download. */
  protected readonly ctDownloadExtension = '.xlsx';

  /*-------------------------- Constructor  --------------------------*/

  constructor(protected service: LicenseServiceGrid
    , protected adminService: AdminService
    , protected serviceAuth: AuthorizationService
    , protected controlResizer: ControlResizerService
    , protected router: Router
    , protected dialogNotification: NotificationDialogService) {

    this.view = service;
  }

  /*----------------------------------- ng  --------------------------*/

  ngOnInit(): void {

    this.req.active = this.licActive;

    this.reqPerm.route = this.router.url;
    this.service.query(this.req);

    forkJoin([
      this.serviceAuth.pagePermissionsGet$(this.reqPerm),
      this.adminService.getAllCompanies()
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([permissions, companies]) => {
        this.perms = permissions.data ?? [];

        this.showAdd = this.perms?.some(i => i.code === "license-create") ?? false;
        this.showEditButton = this.perms?.some(i => i.code === "license-edit") ?? false;
        this.showReadOnlyButton = this.perms?.some(i => i.code === "license-detail") ?? false;
        this.showOneSysSettings = this.perms?.some(i => i.code === "license-detail-onesys") ?? false;

        this.companies = companies;

      });

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /*--------------------------- event handlers  --------------------------*/

  protected onSearchClick(): void {

    this.req.companyId = (this.selectedCompanyItem) ? this.selectedCompanyItem.id : null;
    this.req.active = this.licActive;

    this.state.skip = 0;
    this.req.skip = 0;

    this.service.query(this.req);
  }

  protected onTemplatesClick() {
    this.router.navigate(['./admin/role-template']);
  }

  protected onRolesClick(dataItem: License): void {

    this.router.navigate(
      ['/admin/role']
      , { queryParams: { id: dataItem.id } }
    );
  }

  protected onLicenseUserManageClick(dataItem: License): void {

    this.router.navigate(
      ['/admin/user/license']
      , { queryParams: { id: dataItem.id } }
    );
  }

  protected onUserPermissionsClick(dataItem: License) {
    this.router.navigate(['./admin/user/permission'], { queryParams: { id: dataItem.id } });
  }

  /** Downloads every user in every company as an Excel workbook (permission enforced server-side). */
  protected onDownloadAllUsers(e: Event): void {

    this.downloadUsersFrom(this.adminService.downloadUserStateExcelAll$(), 'All-Companies');
  }

  // -------------- Grid processing ------------------------------------------------

  protected onStateChange(state: State): void {

    this.state = state;

    this.req.skip = this.state.skip;
    this.req.take = this.state.take;

    this.service.query(this.req);
  }

  protected onAddLicense(): void {

    let lic: License = new License();

    lic.isActive = false;
    lic.enforceEndDate = false;
    lic.enforceUsers = false;

    this.editDataItem = lic;

    this.isNew = true;
  }

  protected onEditLicense(args: AddEvent): void {

    this.editDataItem = args.dataItem;
    this.isNew = false;
  }

  protected onCancelLicense(): void {
    this.editDataItem = undefined;
  }

  protected onSaveLicense(item: License): void {

    if (item.id) {

      let merged = { ...this.editDataItem, ...item };

      this.service.save([merged])
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
    else
      this.service.save([item])
        .pipe(takeUntil(this.destroy$))
        .subscribe();

    this.editDataItem = undefined;
  }

  /*--------------------------- internal functions  --------------------------*/

  protected getCompanyName(id: string): string {
    return this.companies!.find(co => co.id == id)!.name ?? '';
  }

  /** Shows the full-page loading overlay. */
  protected loaderShow(): void {

    this.loadingPanelVisible.set(true);
  }

  /** Hides the full-page loading overlay. */
  protected loaderClose(): void {

    this.loadingPanelVisible.set(false);
  }

  /** Builds a fallback file name of the form <Name>-Users-yyyyMMdd.xlsx */
  protected buildDownloadFileName(name: string | null | undefined): string {

    const safe = (name ?? 'Users')
      .replace(/[^\w\- ]+/g, '')
      .trim()
      .replace(/\s+/g, '-');

    const now = new Date();
    const stamp = now.getFullYear().toString()
      + String(now.getMonth() + 1).padStart(2, '0')
      + String(now.getDate()).padStart(2, '0');

    return `${safe}-Users-${stamp}${this.ctDownloadExtension}`;
  }

  /** Triggers a browser save of the supplied blob under the given file name. */
  protected saveBlob(blob: Blob, fileName: string): void {

    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Extracts the file name from a Content-Disposition header.
   * Handles both filename="x" and filename*=UTF-8''x forms. Returns null when absent.
   */
  protected getFileNameFromHeader(header: string | null): string | null {

    if (!header)
      return null;

    const utf8 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
    if (utf8?.[1])
      return decodeURIComponent(utf8[1].trim());

    const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
    if (plain?.[1])
      return plain[1].trim();

    return null;
  }

  /**
   * Runs a workbook download, showing the loader for its duration, and saves
   * the result under the server-supplied name (falling back to a local name).
   */
  protected downloadUsersFrom(source$: Observable<HttpResponse<Blob>>, fallbackName: string | null | undefined): void {

    this.loaderShow();

    source$
      .pipe(
        takeUntil(this.destroy$)
        , finalize(() => this.loaderClose())
      )
      .subscribe({
        next: (resp) => {

          const blob = resp.body;

          if (!blob || blob.size === 0) {
            this.dialogNotification.openNotificationDialog(
              this.ctDownloadDialogTitle,
              'No users were available to download'
            );
            return;
          }

          const fileName = this.getFileNameFromHeader(resp.headers.get('Content-Disposition'))
            ?? this.buildDownloadFileName(fallbackName);

          this.saveBlob(blob, fileName);
        },
        error: () => {
          this.dialogNotification.openNotificationDialog(
            this.ctDownloadDialogTitle,
            'The user download failed. You may not have permission for this download.'
          );
        }
      });
  }
}
